/**
 * One-way hook from shared session logging to platform-specific observability.
 *
 * `configureLogging(session)` calls `notify` whenever the current session
 * changes. The native app registers a callback here so it can update Sentry's
 * user without making this shared package depend on the native-only Sentry SDK.
 * This module holds one callback because the application has one active
 * platform logger. The no-op default leaves web and other callers unchanged.
 */
let observer = () => {};

/**
 * Replaces the platform callback that receives later session changes. This does
 * not replay the current session; the native app clears Sentry during startup
 * and receives later login or logout changes through `notify`.
 *
 * @param {(session: object|null|undefined) => void} nextObserver
 * @returns {void}
 */
function setObserver(nextObserver) {
    observer = nextObserver;
}

/**
 * Passes the current session to the registered platform callback. An empty
 * session represents logout, allowing that callback to clear user context.
 * Callback errors are not caught, so the caller can handle a failed platform
 * update instead of silently continuing with stale context.
 *
 * @param {object|null|undefined} session
 * @returns {void}
 */
function notify(session) {
    observer(session);
}

export default { notify, setObserver };
